import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LoggedInUserWrapper from '../../components/core/LoggedInUserWrapper';
import { homeMenu } from '../../constants';
import { useAuth, useParams } from '../../hooks';
import { useUpdateBadges } from '../../queries';
import HomeIllustration from './HomeIllustration';

function HomeActionCard({ menu, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-2xl p-4 text-left shadow-[0_6px_16px_rgba(0,0,0,0.12)] transition-transform hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(0,0,0,0.14)] active:scale-[0.99]"
      style={{ backgroundColor: menu.bgColor }}
      aria-label={menu.title}
    >
      <span
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-xl"
        style={{ backgroundColor: menu.iconBg }}
        aria-hidden
      >
        {menu.emoji}
      </span>
      <span className="min-w-0 flex-1 pr-1">
        <span
          className="mb-1 block text-lg font-bold tracking-[-0.2px]"
          style={{ color: menu.textColor }}
        >
          {menu.title}
        </span>
        <span className="block text-[13px] leading-[18px] text-[#6B7280]">{menu.subTitle}</span>
      </span>
      <ChevronRight
        className="h-6 w-6 shrink-0 opacity-80"
        style={{ color: menu.textColor }}
        aria-hidden
      />
    </button>
  );
}

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { badges } = useParams();
  const { mutate: updateBadges } = useUpdateBadges();

  const firstName = user?.displayName?.split(' ')?.[0] || 'Student';

  React.useEffect(() => {
    if (badges) {
      updateBadges();
    }
  }, [updateBadges, badges]);

  const onNavigateToPage = (url) => {
    if (url) navigate(url);
  };

  return (
    <LoggedInUserWrapper>
      <div className="h-full min-h-0 overflow-y-auto overscroll-contain bg-white">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12 lg:px-8">
          <section className="mb-6 flex flex-col items-center text-center md:mb-10">
            <p className="mb-2 text-base font-semibold text-[#6B7280] md:text-lg">
              Hi, {firstName} 👋
            </p>
            <h1 className="mb-2 text-[28px] font-bold tracking-[-0.5px] text-[#111827] md:text-4xl">
              Ready to learn?
            </h1>
            <p className="max-w-md text-sm leading-5 text-[#6B7280] md:text-base md:leading-6">
              Choose how you&apos;d like to start your learning journey today
            </p>
          </section>

          <HomeIllustration />

          <section className="mx-auto grid w-full max-w-[400px] grid-cols-1 gap-4 md:max-w-none md:grid-cols-3 md:gap-6">
            {homeMenu.map((menu) => (
              <HomeActionCard
                key={menu.id}
                menu={menu}
                onClick={() => onNavigateToPage(menu.link)}
              />
            ))}
          </section>
        </div>
      </div>
    </LoggedInUserWrapper>
  );
};

export default Home;
